import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Save,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building,
  Calendar,
  ShieldCheck,
  Smartphone,
  Globe,
  Info,
} from "lucide-react";
import Select from "react-select";
import {
  getEmployeeById,
  updateEmployee,
} from "../../../api/hrm/employees/employeeInfo";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import Spinner from "../../../components/ui/Spinner";
import imageCompression from "browser-image-compression";

export default function UserProfileModal({ open, onClose, userId }) {
  const [profile, setProfile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && userId) {
      (async () => {
        try {
          const data = await getEmployeeById(userId);
          setProfile(data);
          setPreview(
            data.avatar_url ||
              "https://cdn-icons-png.flaticon.com/512/149/149071.png"
          );
        } catch (error) {
          showErrorToast("Không thể tải thông tin nhân viên");
        }
      })();
    }
  }, [open, userId]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      try {
        const compressed = await imageCompression(file, {
          maxSizeMB: 2,
          maxWidthOrHeight: 800,
        });
        setPreview(URL.createObjectURL(compressed));
        setProfile((prev) => ({ ...prev, avatarFile: compressed }));
      } catch (error) {
        showErrorToast("Không thể nén ảnh. Vui lòng chọn ảnh khác!");
      }
      return;
    }

    setPreview(URL.createObjectURL(file));
    setProfile((prev) => ({ ...prev, avatarFile: file }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!profile) return;

    const confirm = await showConfirmDialog({
      title: "Xác nhận lưu thay đổi",
      text: "Bạn có chắc muốn cập nhật thông tin cá nhân không?",
      confirmText: "Lưu",
      cancelText: "Hủy",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      let avatarUrl = profile.avatar_url;

      // ✅ Nếu có chọn ảnh mới thì upload lên Google Drive trước
      if (profile.avatarFile) {
        const { uploadEmployeeAvatar } = await import(
          "../../../api/hrm/employees/employeeInfo"
        );
        avatarUrl = await uploadEmployeeAvatar(profile.avatarFile);
      }

      // ✅ Chuẩn bị dữ liệu dạng JSON (thay vì FormData)
      const updatePayload = {
        name: profile.name || null,
        phone: profile.phone || null,
        birthdate: profile.birthdate || null,
        gender: profile.gender || null,
        address: profile.address || null,
        position: profile.position || null,
        avatar_url: avatarUrl || null,
      };

      // ✅ Gọi API cập nhật thông tin
      await updateEmployee(userId, updatePayload);

      showSuccessToast("Cập nhật thông tin thành công!");
      onClose();
    } catch (error) {
      console.error("❌ Lỗi khi lưu thông tin:", error);
      showErrorToast("Không thể lưu thay đổi, vui lòng thử lại.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // Helper để render các item thông tin (Giống StudentDetailModal)
  const renderInputItem = (
    Icon,
    label,
    name,
    value,
    isEditable = true,
    colorClass = "text-gray-500"
  ) => (
    <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-all border border-transparent hover:border-gray-100">
      <div
        className={`p-2.5 rounded-xl bg-white shadow-sm border border-gray-100 ${colorClass}`}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">
          {label}
        </p>
        {isEditable ? (
          <input
            name={name}
            value={value || ""}
            onChange={handleChange}
            className="w-full text-sm font-semibold text-gray-800 bg-transparent border-b border-transparent focus:border-red-500 outline-none pb-1"
            placeholder={`Nhập ${label.toLowerCase()}...`}
          />
        ) : (
          <p className="text-sm font-semibold text-gray-600 italic">
            {value || "Chưa cập nhật"}
          </p>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="relative z-[10000] bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* HEADER BẢN MỚI - NỀN TRẮNG */}
        <div className="relative p-8 bg-white border-b border-gray-100">
          {/* Nút đóng góc phải */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-red-600 transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50"
          >
            <X size={24} />
          </button>

          <div className="flex items-center gap-6">
            {/* Khu vực Avatar */}
            <div className="relative group">
              <div className="w-24 h-24 rounded-[2rem] border-4 border-gray-50 shadow-2xl shadow-red-100/50 overflow-hidden bg-gray-100 ring-1 ring-gray-100">
                <img
                  src={preview}
                  alt="Avatar"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <label className="absolute -bottom-1 -right-1 p-2.5 bg-red-700 text-white rounded-2xl shadow-lg shadow-red-200 cursor-pointer hover:bg-red-800 hover:scale-110 transition-all active:scale-95 border-2 border-white">
                <Camera size={16} />
                <input
                  type="file"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </label>
            </div>

            {/* Thông tin chính */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-black text-gray-800 uppercase tracking-tight">
                  {profile?.name || "Hồ sơ nhân viên"}
                </h2>
                <span className="px-2 py-0.5 bg-green-50 text-green-600 text-[10px] font-black uppercase rounded-lg border border-green-100">
                  Online
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-red-700 text-[11px] font-black uppercase tracking-widest">
                  <ShieldCheck size={14} />
                  {profile?.position || "Chưa có vị trí"}
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full" />
                <div className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">
                  ID: {profile?.employee_code || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT CHIA KHU VỰC (GIỐNG STUDENT DETAIL) */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          {!profile ? (
            <div className="py-20 flex justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* CỘT 1: THÔNG TIN LIÊN HỆ (CHO PHÉP SỬA) */}
              <div className="space-y-6">
                <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1 h-4 bg-red-500 rounded-full" /> Thông tin
                  cá nhân
                </h3>
                <div className="bg-gray-50/50 rounded-[2rem] p-4 space-y-2">
                  {renderInputItem(
                    Smartphone,
                    "Số điện thoại",
                    "phone",
                    profile.phone,
                    true,
                    "text-blue-600"
                  )}
                  {renderInputItem(
                    Mail,
                    "Email cá nhân",
                    "personal_email",
                    profile.personal_email,
                    true,
                    "text-orange-500"
                  )}
                  {renderInputItem(
                    MapPin,
                    "Địa chỉ liên hệ",
                    "address",
                    profile.address,
                    true,
                    "text-green-600"
                  )}
                  {renderInputItem(
                    Calendar,
                    "Ngày sinh",
                    "birthdate",
                    profile.birthdate,
                    true,
                    "text-purple-600"
                  )}
                </div>
              </div>

              {/* CỘT 2: THÔNG TIN CÔNG VIỆC (CHỈ XEM) */}
              <div className="space-y-6">
                <h3 className="text-[11px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1 h-4 bg-indigo-500 rounded-full" /> Thông
                  tin hệ thống
                </h3>
                <div className="bg-gray-50/50 rounded-[2rem] p-4 space-y-2">
                  {renderInputItem(
                    Globe,
                    "Email tổ chức",
                    "email",
                    profile.email,
                    false,
                    "text-indigo-600"
                  )}
                  {renderInputItem(
                    Building,
                    "Chi nhánh",
                    "branch_name",
                    profile.branch_name,
                    false,
                    "text-gray-400"
                  )}
                  {renderInputItem(
                    Briefcase,
                    "Phòng ban",
                    "department_name",
                    profile.department_name,
                    false,
                    "text-gray-400"
                  )}
                  {renderInputItem(
                    Info,
                    "Mã nhân viên",
                    "employee_code",
                    profile.employee_code,
                    false,
                    "text-gray-400"
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-6 border-t border-gray-50 bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl font-black text-gray-400 uppercase text-[10px] tracking-widest hover:text-gray-600 transition-all"
          >
            Đóng
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-red-700 hover:bg-red-800 text-white font-black rounded-xl flex items-center gap-2 shadow-lg shadow-red-100 transition-all active:scale-95 uppercase text-[10px] tracking-widest disabled:opacity-50"
          >
            {saving ? (
              "ĐANG LƯU..."
            ) : (
              <>
                <Save size={18} /> LƯU THAY ĐỔI
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
